import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { tr as __tr } from "../i18n";

/**
 * Promise-based replacement for `window.confirm()`.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   if (await confirm({ title: "Удалить?", description: "..." })) { ... }
 *
 * Mount `<ConfirmDialogProvider>` once near the app root (App.js).
 */

const ConfirmContext = createContext(null);

const DEFAULT_OPTS = {
  title: __tr("Подтвердите действие"),
  description: "",
  confirmText: __tr("Ок"),
  cancelText: __tr("Отмена"),
  destructive: false,
};

export function ConfirmDialogProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState(DEFAULT_OPTS);
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      setOpts({ ...DEFAULT_OPTS, ...options });
      setOpen(true);
      resolverRef.current = resolve;
    });
  }, []);

  const handle = (result) => {
    setOpen(false);
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={open} onOpenChange={(v) => { if (!v) handle(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{opts.title}</DialogTitle>
            {opts.description && (
              <DialogDescription>{opts.description}</DialogDescription>
            )}
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => handle(false)}>
              {opts.cancelText}
            </Button>
            <Button
              onClick={() => handle(true)}
              variant={opts.destructive ? "destructive" : "default"}
            >
              {opts.confirmText}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm must be used inside <ConfirmDialogProvider>");
  }
  return ctx;
}
